import { useEffect, useState } from 'react';
import axios from 'axios';
import { tmAIAssistantDefaultValue } from '../constant';
import { FileText, Bot, User, X, UserRound, BookOpen, Trash2, Plus, BookMarked } from 'lucide-react';
import Loader from '../components/Loader';
import Navbar from '../components/navbar';
import { useTheme } from '../context/ThemeContext';

// ─── Rulebook Modal ────────────────────────────────────────────────────────────
function makeRule(counter) {
  return { id: `prop${counter}`, description: '', courses: [''] };
}

function rulebookToRules(rulebook) {
  return Object.entries(rulebook).map(([key, val]) => ({
    id: key,
    description: val.description || '',
    courses: val.course_title?.length ? val.course_title : [''],
  }));
}

function RulebookModal({ onClose, token }) {
  const [rules, setRules] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set()); // IDs that came from the server
  const [counter, setCounter] = useState(1);
  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Fetch existing rulebook on open
  useEffect(() => {
    const load = async () => {
      try {
        setFetching(true);
        const res = await axios.get(
          `${tmAIAssistantDefaultValue.tmAIChatbotApiUrl}history/admin/get/rulebook`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const rulebook = res?.data?.rulebook || {};
        const loaded = rulebookToRules(rulebook);
        if (loaded.length > 0) {
          setRules(loaded);
          setSavedIds(new Set(loaded.map((r) => r.id)));
          // Set counter beyond existing keys so new rules don't clash
          const maxNum = loaded.reduce((max, r) => {
            const n = parseInt(r.id.replace('prop', ''), 10);
            return isNaN(n) ? max : Math.max(max, n);
          }, 0);
          setCounter(maxNum + 1);
        } else {
          setRules([makeRule(1)]);
          setCounter(2);
        }
      } catch {
        // No existing rulebook — start fresh
        setRules([makeRule(1)]);
        setCounter(2);
      } finally {
        setFetching(false);
      }
    };
    load();
  }, [token]);

  const addRule = () => {
    setRules((prev) => [...prev, makeRule(counter)]);
    setCounter((c) => c + 1);
  };

  // Only unsaved (new) rules can be deleted
  const removeRule = (id) => {
    if (savedIds.has(id)) return;
    setRules((prev) => prev.filter((r) => r.id !== id));
  };

  const updateDesc = (id, value) =>
    setRules((prev) =>
      prev.map((r) => (r.id === id ? { ...r, description: value } : r))
    );

  const addCourse = (id) =>
    setRules((prev) =>
      prev.map((r) => (r.id === id ? { ...r, courses: [...r.courses, ''] } : r))
    );

  const removeCourse = (id, idx) =>
    setRules((prev) =>
      prev.map((r) =>
        r.id === id ? { ...r, courses: r.courses.filter((_, i) => i !== idx) } : r
      )
    );

  const updateCourse = (id, idx, value) =>
    setRules((prev) =>
      prev.map((r) =>
        r.id === id
          ? { ...r, courses: r.courses.map((c, i) => (i === idx ? value : c)) }
          : r
      )
    );

  const handleSubmit = async () => {
    setError('');
    const rulebook = {};
    for (const r of rules) {
      const titles = r.courses.map((c) => c.trim()).filter(Boolean);
      if (!r.description.trim()) {
        setError(`Rule "${r.id}" is missing a description.`);
        return;
      }
      rulebook[r.id] = { description: r.description.trim(), course_title: titles };
    }

    try {
      setSaving(true);
      await axios.post(
        `${tmAIAssistantDefaultValue.tmAIChatbotApiUrl}history/admin/add/rulebook`,
        { rulebook },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onClose();
    } catch (err) {
      setError('Failed to save rulebook. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rulebook-title"
    >
      <div className="bg-panel w-full max-w-2xl max-h-[90vh] rounded-xl shadow-xl border border-glass-border flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center border-b border-glass-border p-4">
          <h2 id="rulebook-title" className="text-lg font-bold text-text-light flex items-center gap-2">
            <BookMarked size={20} />
            Manage Rulebook
          </h2>
          <button
            onClick={onClose}
            aria-label="Close rulebook modal"
            className="p-2 border border-glass-border rounded-md hover:bg-muted focus:ring-2 focus:ring-accent"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-4">
          {fetching && <Loader />}

          {!fetching && rules.length === 0 && (
            <p className="text-text-muted text-center py-8">
              No rules yet. Click "Add Rule" to get started.
            </p>
          )}

          {!fetching && rules.map((rule, ri) => (
            <div
              key={rule.id}
              className="rounded-lg border border-glass-border bg-bg p-4 space-y-4"
            >
              {/* Rule header */}
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold uppercase tracking-widest text-text-muted bg-panel px-2 py-1 rounded border border-glass-border">
                  Rule {ri + 1} · {rule.id}
                </span>
                {savedIds.has(rule.id) ? (
                  <span
                    title="Saved rules cannot be deleted"
                    className="p-1.5 rounded-md border border-glass-border text-text-muted cursor-not-allowed opacity-50 inline-flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                  </span>
                ) : (
                  <button
                    onClick={() => removeRule(rule.id)}
                    aria-label={`Remove rule ${ri + 1}`}
                    className="p-1.5 rounded-md border border-glass-border hover:bg-muted text-red-400 hover:text-red-300"
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-text-muted mb-1.5">
                  Description
                </label>
                <textarea
                  value={rule.description}
                  onChange={(e) => updateDesc(rule.id, e.target.value)}
                  placeholder="Describe this rule..."
                  rows={3}
                  className="w-full bg-panel border border-glass-border rounded-lg px-3 py-2 text-sm text-text-light placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent resize-none"
                />
              </div>

              {/* Course titles */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-text-muted mb-1.5">
                  Course Titles
                </label>
                <div className="space-y-2">
                  {rule.courses.map((course, ci) => (
                    <div key={ci} className="flex gap-2 items-center">
                      <input
                        type="text"
                        value={course}
                        onChange={(e) => updateCourse(rule.id, ci, e.target.value)}
                        placeholder={`Course title ${ci + 1}`}
                        className="flex-1 bg-panel border border-glass-border rounded-lg px-3 py-2 text-sm text-text-light placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent"
                      />
                      {rule.courses.length > 1 && (
                        <button
                          onClick={() => removeCourse(rule.id, ci)}
                          aria-label={`Remove course ${ci + 1}`}
                          className="p-1.5 rounded-md border border-glass-border hover:bg-muted text-text-muted hover:text-red-400"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => addCourse(rule.id)}
                  className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-semibold text-accent border border-dashed border-accent rounded-md px-3 py-1.5 hover:bg-muted transition"
                >
                  <Plus size={12} />
                  Add Course
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="border-t border-glass-border p-4">
          {error && (
            <p role="alert" className="text-red-400 text-sm mb-3">
              {error}
            </p>
          )}
          <div className="flex justify-between items-center">
            <button
              onClick={addRule}
              disabled={fetching}
              className="inline-flex items-center gap-2 rounded-lg border border-glass-border px-4 py-2 text-sm font-semibold text-text-light hover:bg-muted transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus size={15} />
              Add Rule
            </button>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="rounded-lg border border-glass-border px-4 py-2 text-sm font-semibold text-text-light hover:bg-muted transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving || fetching || rules.length === 0 || rules.some((r) => !r.description.trim() || r.courses.every((c) => !c.trim()))}
                className="rounded-lg bg-accent border border-accent px-4 py-2 text-sm font-semibold text-[color:var(--accent-foreground)] hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving…' : 'Save Rulebook'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Dashboard ────────────────────────────────────────────────────────────
export default function AdminDashboard() {
  const { theme, setTheme } = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [chatData, setChatData] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [persona, setPersona] = useState('');
  const [personaInformation, setPersonaInformation] = useState('');
  const [selectedUserMeta, setSelectedUserMeta] = useState(null);
  const [modalView, setModalView] = useState('transcript');
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);
  const [showRulebookModal, setShowRulebookModal] = useState(false);

  const token = localStorage.getItem('authToken');

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    window.history.pushState({ isAdminDashboard: true }, '', window.location.href);
    const handlePopState = () => window.history.forward();
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const applyThemeChoice = (nextTheme) => {
    document.documentElement.setAttribute('data-theme', nextTheme);
    document.body?.setAttribute('data-theme', nextTheme);
    localStorage.setItem('themeMode', 'manual');
    localStorage.setItem('theme', nextTheme);
    setTheme(nextTheme);
  };

  useEffect(() => {
    const handleShortcut = (e) => {
      const key = typeof e.key === 'string' ? e.key.toLowerCase() : '';
      if (key !== 'i' && e.code !== 'KeyI') return;
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || document.activeElement?.isContentEditable) return;
      e.preventDefault();
      applyThemeChoice(theme === 'dark' ? 'light' : 'dark');
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [theme]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${tmAIAssistantDefaultValue.tmAIChatbotApiUrl}history/admin/users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(response?.data?.data || []);
    } catch (err) {
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchChatHistory = async (user, view = 'transcript') => {
    try {
      setChatLoading(true);
      setSelectedSession(user.email);
      setSelectedUserMeta(user);
      setModalView(view);
      const response = await axios.post(
        `${tmAIAssistantDefaultValue.tmAIChatbotApiUrl}history/chat/session`,
        { email: user.email, interview_no: user.interview_no },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setChatData(response?.data?.question_answers || []);
      setCourses(response?.data?.courses || []);
      setPersona(
        response?.data?.persona ||
          response?.data?.profile?.persona ||
          user?.persona ||
          'Not available'
      );
      setPersonaInformation(response?.data?.persona_information);
    } catch (err) {
      console.error('Failed to fetch chat history');
    } finally {
      setChatLoading(false);
    }
  };

  const deleteUser = async (user) => {
    const confirmed = window.confirm(`Delete user ${user.email}? This cannot be undone.`);
    if (!confirmed) return;
    const deleteKey = `${user.session_id}-${user.interview_no || 'na'}-${user.email || ''}`;
    setDeleteLoadingId(deleteKey);
    setError('');
    try {
      await axios.delete(
        `${tmAIAssistantDefaultValue.tmAIChatbotApiUrl}history/admin/delete-user`,
        {
          data: { email: user.email, interview_no: user.interview_no },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (selectedSession === user.email) closeModal();
      await fetchUsers();
    } catch (err) {
      setError('Failed to delete user');
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const closeModal = () => {
    setSelectedSession(null);
    setChatData([]);
    setCourses([]);
    setPersona('');
    setSelectedUserMeta(null);
    setModalView('transcript');
  };

  const getCourseLink = (course, linkType = 'video') => {
    const keys =
      linkType === 'video'
        ? ['video_url', 'videoLink', 'video', 'youtube_url', 'youtube', 'resource_url', 'link', 'url']
        : ['pdf_url', 'pdfLink', 'pdf', 'document_url', 'resource_url', 'link', 'url'];
    const isPdf = (url = '') => url.toLowerCase().includes('.pdf');
    const candidates = keys
      .map((key) => course?.[key])
      .filter((value) => typeof value === 'string' && value.startsWith('http'));
    if (!candidates.length) return null;
    if (linkType === 'pdf') return candidates.find(isPdf) || null;
    return candidates.find((url) => !isPdf(url)) || null;
  };

  const getPrimaryCourseLink = (course) => {
    const preferredPdf = getCourseLink(course, 'pdf');
    if (preferredPdf) return { href: preferredPdf, label: 'Open PDF', type: 'pdf' };
    const preferredVideo = getCourseLink(course, 'video');
    if (preferredVideo) return { href: preferredVideo, label: 'Open Video', type: 'video' };
    return null;
  };

  const modalTitleByView = {
    transcript: `Transcript - ${selectedSession}`,
    persona: `Customer Assessment - ${selectedSession}`,
    courses: `Personalised learning pathway - ${selectedSession}`,
  };

  return (
    <>
      <Navbar />

      <main className="bg-panel px-3 sm:px-6 lg:px-8 pb-8">
        <section className="max-w-7xl mx-auto">
          <header className="mb-8">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-text-light">Users</h1>
                <p className="mt-2 text-base text-text-muted">
                  Press <strong>I</strong> or use the buttons to switch light and dark mode.
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                {/* ── Rulebook button ── */}
                {/* <button
                  type="button"
                  onClick={() => setShowRulebookModal(true)}
                  className="inline-flex items-center gap-2 rounded-lg border border-glass-border px-4 py-2 text-base font-semibold text-text-light bg-bg hover:bg-muted transition focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  <BookMarked size={18} />
                  Add Rulebook
                </button> */}

                <button
                  type="button"
                  onClick={() => applyThemeChoice('light')}
                  className={`rounded-lg border px-4 py-2 text-base font-semibold transition focus:outline-none focus:ring-2 focus:ring-accent ${
                    theme === 'light'
                      ? 'bg-accent text-[color:var(--accent-foreground)] border-accent shadow-md'
                      : 'bg-bg text-text-light border-glass-border hover:bg-muted'
                  }`}
                  aria-pressed={theme === 'light'}
                >
                  Light Mode
                </button>
                <button
                  type="button"
                  onClick={() => applyThemeChoice('dark')}
                  className={`rounded-lg border px-4 py-2 text-base font-semibold transition focus:outline-none focus:ring-2 focus:ring-accent ${
                    theme === 'dark'
                      ? 'bg-accent text-[color:var(--accent-foreground)] border-accent shadow-md'
                      : 'bg-bg text-text-light border-glass-border hover:bg-muted'
                  }`}
                  aria-pressed={theme === 'dark'}
                >
                  Dark Mode
                </button>
              </div>
            </div>
          </header>

          {loading && <Loader />}
          {error && (
            <p role="alert" className="text-red-700 font-semibold">
              {error}
            </p>
          )}

          {!loading && !error && (
            <div className="rounded-xl border border-glass-border">
              <div className="w-full overflow-x-auto">
                <table className="min-w-[760px] w-full border-collapse text-left">
                  <thead className="bg-bg text-text-light text-sm">
                    <tr className="text-center">
                      <th scope="col" className="p-4">Sr. No.</th>
                      <th scope="col" className="p-4">Email</th>
                      <th scope="col" className="p-4">Created Date</th>
                      <th scope="col" className="p-4">Type</th>
                      <th scope="col" className="p-4">Status</th>
                      <th scope="col" className="p-4">Action</th>
                    </tr>
                  </thead>

                  <tbody className="text-center">
                    {users.map((user, index) => {
                      const rowKey = `${user.session_id}-${user.interview_no || 'na'}-${user.email || index}`;
                      const isDeleting = deleteLoadingId === rowKey;

                      return (
                        <tr
                          key={rowKey}
                          className="border-t border-glass-border hover:bg-muted focus-within:bg-muted text-text-light"
                        >
                          <td className="p-4">{index + 1}</td>
                          <td className="p-4 break-all">{user.email}</td>
                          <td className="p-4">{new Date(user.created_at).toLocaleString()}</td>
                          <td className="p-4 break-all">{user.interview_type}</td>
                          <td className="p-4">
                            <span
                              style={
                                theme === 'dark'
                                  ? { backgroundColor: '#FFD700', color: '#000000' }
                                  : {}
                              }
                              className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                                user.is_completed
                                  ? theme === 'dark'
                                    ? ''
                                    : 'bg-green-700 text-white'
                                  : 'bg-yellow-600 text-white'
                              }`}
                            >
                              {user.is_completed ? 'Completed' : 'Pending'}
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="relative group">
                                <button
                                  onClick={() => fetchChatHistory(user, 'transcript')}
                                  aria-label={`View transcript for ${user.email}`}
                                  title="Transcript"
                                  className="inline-flex items-center justify-center p-2 rounded-md border border-glass-border hover:bg-muted focus:outline-none focus:ring-2 focus:ring-accent"
                                >
                                  <FileText size={22} />
                                </button>
                                <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 rounded bg-bg px-2 py-1 text-xs text-text-light opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 whitespace-nowrap">
                                  Transcript
                                </span>
                              </div>
                              <div className="relative group">
                                <button
                                  onClick={() => fetchChatHistory(user, 'persona')}
                                  aria-label={`View persona for ${user.email}`}
                                  title="Persona"
                                  className="inline-flex items-center justify-center p-2 rounded-md border border-glass-border hover:bg-muted focus:outline-none focus:ring-2 focus:ring-accent"
                                >
                                  <UserRound size={18} />
                                </button>
                                <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 rounded bg-bg px-2 py-1 text-xs text-text-light opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 whitespace-nowrap">
                                  Customer Assessment
                                </span>
                              </div>
                              <div className="relative group">
                                <button
                                  onClick={() => fetchChatHistory(user, 'courses')}
                                  aria-label={`View recommended courses for ${user.email}`}
                                  title="Recommended Courses"
                                  className="inline-flex items-center justify-center p-2 rounded-md border border-glass-border hover:bg-muted focus:outline-none focus:ring-2 focus:ring-accent"
                                >
                                  <BookOpen size={18} />
                                </button>
                                <span className="pointer-events-none absolute -top-8 left-1/4 -translate-x-1/2 rounded bg-bg px-2 py-1 text-xs text-text-light opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 whitespace-nowrap">
                                  Recommended Courses
                                </span>
                              </div>
                              <div className="relative group">
                                <button
                                  onClick={() => deleteUser(user)}
                                  aria-label={`Delete user ${user.email}`}
                                  title="Delete User"
                                  disabled={isDeleting}
                                  className="inline-flex items-center justify-center p-2 rounded-md border border-glass-border hover:bg-muted focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <Trash2 size={18} />
                                </button>
                                <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 rounded bg-bg px-2 py-1 text-xs text-text-light opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 whitespace-nowrap">
                                  {isDeleting ? 'Deleting...' : 'Delete User'}
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Chat / Persona / Courses Modal */}
          {selectedSession && (
            <div
              className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50"
              role="dialog"
              aria-modal="true"
              aria-labelledby="chat-title"
            >
              <div className="bg-panel w-full max-w-6xl h-[90vh] rounded-xl shadow-xl border border-glass-border flex flex-col">
                <div className="flex justify-between items-center border-b p-4">
                  <h2 id="chat-title" className="text-lg font-bold text-text-light truncate">
                    {modalTitleByView[modalView] || `Review - ${selectedSession}`}
                  </h2>
                  <button
                    onClick={closeModal}
                    aria-label="Close chat history"
                    className="p-2 border border-glass-border rounded-md hover:bg-muted focus:ring-2 focus:ring-accent"
                  >
                    <X size={22} />
                  </button>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-6">
                  {chatLoading && <Loader />}

                  {!chatLoading && modalView === 'transcript' && chatData.length === 0 && (
                    <p className="text-text-muted text-center">No chat history found.</p>
                  )}

                  {!chatLoading && modalView === 'transcript' && (
                    <ol className="space-y-4">
                      {chatData.map((chat, index) => (
                        <li
                          key={index}
                          className="list-none rounded-lg border border-glass-border bg-panel-bg p-4 space-y-4"
                        >
                          <div className="flex items-start gap-3">
                            <Bot size={24} aria-hidden="true" />
                            <div className="w-full bg-muted p-4 rounded-lg text-sm text-left">
                              <strong>Q{index + 1}:</strong> {chat.question}
                            </div>
                          </div>
                          <div className="flex items-start gap-3">
                            <User size={24} aria-hidden="true" />
                            <div className="w-full bg-bg text-text-light p-4 rounded-lg text-sm text-left">
                              <strong>A{index + 1}:</strong> {chat.answer}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}

                  {!chatLoading && modalView === 'persona' && (
                    <div className="border border-glass-border rounded-lg p-5 bg-panel-bg">
                      <h3 className="text-lg font-bold mb-3">Customer Assessment</h3>
                      <div className="text-sm leading-relaxed whitespace-pre-wrap">
                        {personaInformation}
                      </div>
                      <div className="text-sm leading-relaxed whitespace-pre-wrap">
                        RNIB V.I. Persona: {persona || 'Persona not available.'}
                      </div>
                    </div>
                  )}

                  {!chatLoading && modalView === 'courses' && (
                    <div>
                      {courses.length === 0 && (
                        <p className="text-text-muted text-center">No recommended courses found.</p>
                      )}
                      <div className="space-y-3">
                        {courses.map((course, index) => (
                          <div
                            key={index}
                            className="bg-panel-bg border border-glass-border rounded-lg p-4 text-sm"
                          >
                            <p className="font-semibold">{course.title}</p>
                            <div className="mt-3 flex gap-3 flex-wrap">
                              {(() => {
                                const primaryLink = getPrimaryCourseLink(course);
                                if (!primaryLink) return null;
                                const isPdf = primaryLink.type === 'pdf';
                                return (
                                  <a
                                    href={primaryLink.href}
                                    target="_blank"
                                    rel="noreferrer"
                                    {...(isPdf ? { download: true } : {})}
                                    className={`underline ${isPdf ? 'text-yellow-accent' : 'text-teal-accent'}`}
                                  >
                                    {primaryLink.label}
                                  </a>
                                );
                              })()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Rulebook Modal */}
          {showRulebookModal && (
            <RulebookModal
              token={token}
              onClose={() => setShowRulebookModal(false)}
            />
          )}
        </section>
      </main>
    </>
  );
}